import { Menu as BaseMenu } from '@base-ui/react/menu'
import type { ComponentProps, ReactElement, ReactNode } from 'react'
import { IconCheck } from './icons'
import styles from './styles/menu.module.css'

export interface MenuProps extends ComponentProps<typeof BaseMenu.Root> {
  trigger: ReactElement
  children: ReactNode
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
}

/**
 * Menú de acciones. Cada item **hace** algo al elegirlo — no es un selector de valor; para eso
 * está `Select`, que además anuncia el valor elegido.
 */
export function Menu({ trigger, children, align = 'start', side = 'bottom', className, ...props }: MenuProps) {
  return (
    <BaseMenu.Root {...props}>
      <BaseMenu.Trigger render={trigger} />
      <BaseMenu.Portal>
        <BaseMenu.Positioner className={styles['au-menu-positioner']} align={align} side={side} sideOffset={6}>
          <BaseMenu.Popup className={[styles['au-menu'], className].filter(Boolean).join(' ')}>{children}</BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  )
}

export interface MenuItemProps extends ComponentProps<typeof BaseMenu.Item> {
  icon?: ReactNode
  /** Atajo de teclado, alineado a la derecha. Solo decorativo: el binding lo pone la página. */
  shortcut?: string
  danger?: boolean
}

export function MenuItem({ icon, shortcut, danger = false, className, children, ...props }: MenuItemProps) {
  const classes = [styles['au-menu-item'], danger && styles['au-menu-item--danger'], className].filter(Boolean).join(' ')
  return (
    <BaseMenu.Item className={classes} {...props}>
      {icon && <span className={styles['au-menu-item-icon']}>{icon}</span>}
      <span>{children}</span>
      {shortcut && <span className={styles['au-menu-item-shortcut']}>{shortcut}</span>}
    </BaseMenu.Item>
  )
}

export function MenuCheckboxItem({ className, children, ...props }: ComponentProps<typeof BaseMenu.CheckboxItem>) {
  return (
    <BaseMenu.CheckboxItem className={[styles['au-menu-item'], className].filter(Boolean).join(' ')} {...props}>
      <BaseMenu.CheckboxItemIndicator className={styles['au-menu-item-indicator']} keepMounted>
        <IconCheck width={12} height={12} />
      </BaseMenu.CheckboxItemIndicator>
      <span>{children}</span>
    </BaseMenu.CheckboxItem>
  )
}

export function MenuRadioItem({ className, children, ...props }: ComponentProps<typeof BaseMenu.RadioItem>) {
  return (
    <BaseMenu.RadioItem className={[styles['au-menu-item'], className].filter(Boolean).join(' ')} {...props}>
      <BaseMenu.RadioItemIndicator className={styles['au-menu-item-indicator']} keepMounted>
        <IconCheck width={12} height={12} />
      </BaseMenu.RadioItemIndicator>
      <span>{children}</span>
    </BaseMenu.RadioItem>
  )
}

export function MenuSeparator({ className, ...props }: ComponentProps<typeof BaseMenu.Separator>) {
  return <BaseMenu.Separator className={[styles['au-menu-separator'], className].filter(Boolean).join(' ')} {...props} />
}

export function MenuGroupLabel({ className, ...props }: ComponentProps<typeof BaseMenu.GroupLabel>) {
  return <BaseMenu.GroupLabel className={[styles['au-menu-group-label'], className].filter(Boolean).join(' ')} {...props} />
}
